import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsApi, parksApi, jobsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserPlus, Briefcase } from 'lucide-react';
import { format } from 'date-fns';

const leadStatuses = ['new', 'contacted', 'qualified', 'converted', 'lost'];
const leadSources = ['Website', 'Referral', 'Insurance Partner', 'Phone', 'Email', 'Other'];

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [parks, setParks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: '',
    status: 'new',
    notes: '',
    park_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsData, parksData] = await Promise.all([
        leadsApi.getAll(),
        parksApi.getAll(),
      ]);
      setLeads(leadsData);
      setParks(parksData);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', source: '', status: 'new', notes: '', park_id: 'none' });
    setEditingLead(null);
  };

  const openDialog = (lead = null) => {
    if (lead) {
      setEditingLead(lead);
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || '',
        status: lead.status || 'new',
        notes: lead.notes || '',
        park_id: lead.park_id || 'none',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert "none" values to empty strings for backend
      const submitData = {
        ...formData,
        park_id: formData.park_id === 'none' ? '' : formData.park_id
      };
      
      if (editingLead) {
        await leadsApi.update(editingLead.id, submitData);
        toast.success('Lead updated');
      } else {
        await leadsApi.create(submitData);
        toast.success('Lead created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save lead');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await leadsApi.delete(lead.id);
      toast.success('Lead deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleConvert = async (lead) => {
    if (!window.confirm('Convert this lead to a client?')) return;
    try {
      await leadsApi.convert(lead.id);
      toast.success('Lead converted to client');
      fetchData();
    } catch (error) {
      toast.error('Failed to convert lead');
    }
  };

  const handleConvertToJob = async (lead) => {
    if (!window.confirm('Create a new job from this lead? This will also convert the lead to a client.')) return;
    try {
      // First convert to client
      const client = await leadsApi.convert(lead.id);
      
      // Get park info for address
      const park = parks.find(p => p.id === lead.park_id);
      
      // Create job with lead data
      const jobData = {
        title: `Enquiry from ${lead.name}`,
        description: lead.notes || '',
        client_id: client.id,
        park_id: lead.park_id,
        property_address: park?.address || '',
        property_postcode: '',
        policyholder_name: lead.name,
        policyholder_tel: lead.phone,
        policyholder_email: lead.email,
        job_type: 'inspection',
      };
      
      await jobsApi.create(jobData);
      toast.success('Lead converted to client and job created!');
      navigate('/jobs');
    } catch (error) {
      console.error('Failed to convert lead to job:', error);
      toast.error('Failed to create job from lead');
    }
  };

  const getParkName = (parkId) => {
    const park = parks.find(p => p.id === parkId);
    return park?.name || '-';
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'source', label: 'Source' },
    { key: 'park_id', label: 'Park', render: (val) => getParkName(val) },
    { key: 'status', label: 'Status', render: (val) => <StatusBadge status={val} /> },
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const getLeadActions = (lead) => {
    const actions = [
      { label: 'Edit', icon: Pencil, onClick: openDialog },
    ];
    
    // Only show conversion options if not already converted
    if (lead.status !== 'converted' && lead.status !== 'lost') {
      actions.push({ label: 'Create Job', icon: Briefcase, onClick: handleConvertToJob, className: 'text-blue-600' });
      actions.push({ label: 'Convert to Client', icon: UserPlus, onClick: handleConvert, className: 'text-green-600' });
    }
    
    actions.push({ label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true });
    
    return actions;
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="text-sm text-stone-500 mt-1">Manage potential clients</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-lead-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Lead
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={leads}
        actions={getLeadActions}
        isLoading={isLoading}
        emptyMessage="No leads found. Add your first lead!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="lead-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="lead-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="lead-phone-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source">Source</Label>
                <Select value={formData.source} onValueChange={(val) => setFormData({ ...formData, source: val })}>
                  <SelectTrigger data-testid="lead-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadSources.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger data-testid="lead-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="park">Park/Site</Label>
              <Select value={formData.park_id} onValueChange={(val) => setFormData({ ...formData, park_id: val })}>
                <SelectTrigger data-testid="lead-park-select">
                  <SelectValue placeholder="Select park" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="lead-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="lead-submit-btn">
                {editingLead ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
